import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconSelector,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn, relativeTime } from '@/lib/utils';
import { api, type UserRow } from '../api.js';

type SortKey = 'user' | 'fullName' | 'email' | 'accessAt';
type SortDir = 'asc' | 'desc';

export function UsersPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: api.users,
  });
  const [sortKey, setSortKey] = useState<SortKey>('user');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sorted = useMemo(() => {
    if (!data) return [];
    const rows = [...data.users];
    rows.sort((a, b) => cmp(a, b, sortKey) * (sortDir === 'asc' ? 1 : -1));
    return rows;
  }, [data, sortKey, sortDir]);

  function handleSort(key: SortKey): void {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Numeric / date columns feel more natural starting newest-first;
      // text columns feel natural a→z. accessAt is a date — default desc.
      setSortDir(key === 'accessAt' ? 'desc' : 'asc');
    }
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Users</h1>
        {data && <Badge>{data.users.length}</Badge>}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2">
          <Spinner />
          <span className="text-sm text-muted-foreground">Loading users…</span>
        </div>
      )}

      {error && (
        <Alert tone="destructive">
          <IconAlertCircle />
          <AlertTitle>Failed to load users</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="rounded-md border border-neutral-800 bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead label="User" sortKey="user" current={sortKey} dir={sortDir} onSort={handleSort} />
                <SortableHead label="Full name" sortKey="fullName" current={sortKey} dir={sortDir} onSort={handleSort} />
                <SortableHead label="Email" sortKey="email" current={sortKey} dir={sortDir} onSort={handleSort} />
                <SortableHead label="Last access" sortKey="accessAt" current={sortKey} dir={sortDir} onSort={handleSort} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((u) => (
                <TableRow key={u.user}>
                  <TableCell className="font-mono">
                    <Link
                      to={`/users/${encodeURIComponent(u.user)}`}
                      className="text-theia-400 hover:underline"
                    >
                      {u.user}
                    </Link>
                  </TableCell>
                  <TableCell>{u.fullName ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{relativeTime(u.accessAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function SortableHead({
  label,
  sortKey,
  current,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
}) {
  const active = sortKey === current;
  const Icon = !active ? IconSelector : dir === 'asc' ? IconArrowUp : IconArrowDown;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider transition-colors',
          active ? 'text-theia-400' : 'hover:text-foreground',
        )}
      >
        {label}
        <Icon size={12} stroke={2} />
      </button>
    </TableHead>
  );
}

/**
 * Stable comparator. Nulls always sort last (regardless of direction)
 * so empty cells don't pile up at the top when sorting ascending.
 */
function cmp(a: UserRow, b: UserRow, key: SortKey): number {
  const av = a[key];
  const bv = b[key];
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  if (key === 'accessAt') {
    return new Date(av).getTime() - new Date(bv).getTime();
  }
  return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
}

